/* Feb-2026 · Zernio Analytics card — Author Business Dashboard.
 *
 * Two panes:
 *   • Cost-per-sale + ROI verdict per platform (from `/zernio/analytics/cost-per-sale`)
 *   • Best-bang recommendations (from `/zernio/analytics/platform-recommendations`)
 *
 * Self-hides when the `zernio_publishing` flag is off.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, TrendingUp, TrendingDown, Minus, Lightbulb } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '@/lib/api';

function useVerdictStyle(t) {
  return {
    scale_up:          { cls: 'text-emerald-700 bg-emerald-500/10 border-emerald-500/30', icon: <TrendingUp className="h-3 w-3" />, label: t('zernio.verdictScaleUp') },
    hold:              { cls: 'text-amber-700 bg-amber-500/10 border-amber-500/30',       icon: <Minus className="h-3 w-3" />,      label: t('zernio.verdictHold') },
    reduce_or_pause:   { cls: 'text-rose-700 bg-rose-500/10 border-rose-500/30',          icon: <TrendingDown className="h-3 w-3" />, label: t('zernio.verdictReduce') },
    insufficient_data: { cls: 'text-muted-foreground bg-muted border-border',              icon: <Minus className="h-3 w-3" />,      label: t('zernio.verdictNoData') },
  };
}

const usd = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`);

export default function ZernioAnalyticsCard() {
  const { t } = useTranslation();
  const VERDICT_STYLE = useVerdictStyle(t);
  const [cfg, setCfg]           = useState(null);
  const [cost, setCost]         = useState(null);
  const [reco, setReco]         = useState(null);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const c = await fetch(`${API_BASE}/zernio/config`, { credentials: 'include' }).then((r) => r.ok ? r.json() : null);
        if (!alive) return;
        setCfg(c);
        if (!c?.flag_on || !c?.configured) { setLoading(false); return; }
        const [cs, rc] = await Promise.all([
          fetch(`${API_BASE}/zernio/analytics/cost-per-sale`, { credentials: 'include' }).then((r) => r.ok ? r.json() : null),
          fetch(`${API_BASE}/zernio/analytics/platform-recommendations`, { credentials: 'include' }).then((r) => r.ok ? r.json() : null),
        ]);
        if (!alive) return;
        setCost(cs); setReco(rc);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!cfg || !cfg.flag_on || !cfg.configured) return null;

  return (
    <Card data-testid="zernio-analytics-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-[13px]">
          <Lightbulb className="h-4 w-4 text-primary" /> {t('zernio.cardTitle')}
        </CardTitle>
        <p className="text-[11.5px] text-muted-foreground">
          {t('zernio.cardDescription')}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && (
          <div className="py-6 text-center text-[12px] text-muted-foreground">
            <Loader2 className="mx-auto h-4 w-4 animate-spin" />
          </div>
        )}

        {!loading && cost && (
          <div>
            <div className="mb-2 flex items-center justify-between text-[11px]">
              <span className="uppercase tracking-wider text-muted-foreground">{t('zernio.costPerSale')}</span>
              <span className="font-mono-stat">
                <span className="text-muted-foreground">{t('zernio.royalties')} </span>
                <b className="text-emerald-700">{usd(cost.totals?.royalty_usd)}</b>
                <span className="mx-2 text-muted-foreground">·</span>
                <span className="text-muted-foreground">{t('zernio.spend')} </span>
                <b className="text-rose-700">{usd(cost.totals?.ad_spend_usd)}</b>
                {cost.totals?.roi_pct != null && (
                  <>
                    <span className="mx-2 text-muted-foreground">·</span>
                    <span className="text-muted-foreground">{t('zernio.roi')} </span>
                    <b className={cost.totals.roi_pct >= 100 ? 'text-emerald-700' : 'text-rose-700'} data-testid="zernio-total-roi">
                      {cost.totals.roi_pct}%
                    </b>
                  </>
                )}
              </span>
            </div>
            <div className="overflow-hidden rounded-md border border-border">
              <table className="w-full text-[11.5px]">
                <thead className="bg-muted/50 text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-2 py-1.5 text-left">{t('zernio.colPlatform')}</th>
                    <th className="px-2 py-1.5 text-right">{t('zernio.colClicks')}</th>
                    <th className="px-2 py-1.5 text-right">{t('zernio.colSpend')}</th>
                    <th className="px-2 py-1.5 text-right">{t('zernio.colAttributedRoyalty')}</th>
                    <th className="px-2 py-1.5 text-right">{t('zernio.colPerClick')}</th>
                    <th className="px-2 py-1.5 text-right">{t('zernio.colRoi')}</th>
                    <th className="px-2 py-1.5 text-right">{t('zernio.colVerdict')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(cost.per_platform || []).length === 0 ? (
                    <tr><td colSpan="7" className="px-2 py-4 text-center text-muted-foreground">
                      {t('zernio.noActivity')}
                    </td></tr>
                  ) : cost.per_platform.map((p) => {
                    const v = VERDICT_STYLE[p.verdict] || VERDICT_STYLE.insufficient_data;
                    return (
                      <tr key={p.platform} className="border-t border-border/60"
                          data-testid={`zernio-cps-row-${p.platform}`}>
                        <td className="px-2 py-1.5 font-semibold capitalize">{p.platform}</td>
                        <td className="px-2 py-1.5 text-right font-mono-stat">{p.clicks}</td>
                        <td className="px-2 py-1.5 text-right font-mono-stat text-rose-700">{usd(p.ad_spend_usd)}</td>
                        <td className="px-2 py-1.5 text-right font-mono-stat text-emerald-700">{usd(p.attributed_royalty_usd)}</td>
                        <td className="px-2 py-1.5 text-right font-mono-stat">{usd(p.cost_per_click_usd)}</td>
                        <td className="px-2 py-1.5 text-right font-mono-stat">{p.roi_pct == null ? '—' : `${p.roi_pct}%`}</td>
                        <td className="px-2 py-1.5 text-right">
                          <span className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-[1px] text-[10px] ${v.cls}`}>
                            {v.icon} {v.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && reco && (
          <div>
            <div className="mb-2 text-[10.5px] uppercase tracking-wider text-muted-foreground">
              {t('zernio.recommendedOrder')} {reco.recommend_top?.join(' → ')}
            </div>
            <div className="overflow-hidden rounded-md border border-border">
              <table className="w-full text-[11.5px]">
                <thead className="bg-muted/50 text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-2 py-1.5 text-left">{t('zernio.colPlatform')}</th>
                    <th className="px-2 py-1.5 text-right">{t('zernio.colExpectedClicks')}</th>
                    <th className="px-2 py-1.5 text-right">{t('zernio.colPaidPer100')}</th>
                    <th className="px-2 py-1.5 text-right">{t('zernio.colPersonalisation')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(reco.ranked || []).map((r) => (
                    <tr key={r.platform} className="border-t border-border/60"
                        data-testid={`zernio-reco-row-${r.platform}`}>
                      <td className="px-2 py-1.5 font-semibold capitalize">{r.platform}</td>
                      <td className="px-2 py-1.5 text-right font-mono-stat">{r.expected_clicks_per_post}</td>
                      <td className="px-2 py-1.5 text-right font-mono-stat">
                        {r.supports_paid_ads ? usd(r.paid_cost_per_100_clicks_usd) : <span className="text-muted-foreground">{t('zernio.na')}</span>}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono-stat">{r.personalisation_weight_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-[10.5px] italic text-muted-foreground">{reco.explanation}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
